import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import html2canvas from 'html2canvas';
import styles from './TabQRCode.module.css';
import MetaHead from '../components/MetaHead';

/* ============================================================
   TabQRCode — onglet "QR Code & lien direct"
   ------------------------------------------------------------
   Format sticker : logo Revyo + label + QR + nom resto + tagline
   + bandeau "Votre avis compte pour nous" + footer.
   Le téléchargement / impression capture TOUTE la carte sticker.

   i18n P5b : useTranslation('tabs') + MetaHead page="qr_code".
   Les textes capturés dans le sticker passent par t() — ils sont donc
   localisés en EN/ES/IT au moment du download / print.
   ============================================================ */

/** Logo Revyo SVG en data URI (utilisé au centre du QR). */
const REVYO_LOGO_SVG =
  'data:image/svg+xml;base64,' +
  btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36">
    <rect width="36" height="36" rx="9" fill="#E8502A"/>
    <text x="18" y="26" text-anchor="middle"
      font-family="Inter, system-ui, sans-serif"
      font-weight="800" font-size="22" fill="white">R</text>
  </svg>`);

function KPI({ icon, label, value, accent }) {
  return (
    <div className={styles.kpi}>
      <div className={styles.kpiIcon} style={accent ? { background: accent } : undefined}>{icon}</div>
      <div className={styles.kpiBody}>
        <div className={styles.kpiValue}>{value}</div>
        <div className={styles.kpiLabel}>{label}</div>
      </div>
    </div>
  );
}

/* ============================================================
   TabQRCode
============================================================ */
export default function TabQRCode({
  restoName = 'Mon Restaurant',
  reviewLink = 'https://www.getrevyo.com/r/demo',
  stats = { scans: 0, avisGeneres: 0, tauxConversion: 0, feedbacks: 0 },
}) {
  const { t } = useTranslation('tabs');
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const inputRef        = useRef(null);
  const stickerRef      = useRef(null); // toute la carte sticker (logo + QR + texte) — capture pour télécharger
  const hiddenCanvasRef = useRef(null); // QRCodeCanvas caché 1024px pour PNG haute résolution

  /** Copier le lien dans le presse-papiers. */
  const handleCopy = useCallback(async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(reviewLink);
      } else if (inputRef.current) {
        inputRef.current.select();
        document.execCommand('copy');
      }
      setCopied(true);
    } catch (e) { /* noop */ }
  }, [reviewLink]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const triggerDownload = (href, filename) => {
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const fileSlug = restoName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'revyo';

  /** Télécharger TOUTE la carte sticker en PNG (logo + QR + nom + tagline). */
  const handleDownloadPNG = useCallback(async () => {
    setDownloading('png');
    try {
      if (!stickerRef.current) throw new Error(t('qr_code.err_sticker_not_found'));
      const canvas = await html2canvas(stickerRef.current, {
        backgroundColor: '#FFFFFF',
        scale: 3,
        useCORS: true,
      });
      canvas.toBlob((blob) => {
        if (!blob) {
          setDownloading(null);
          return alert(t('qr_code.err_png_generation'));
        }
        const url = URL.createObjectURL(blob);
        triggerDownload(url, `revyo-qr-${fileSlug}.png`);
        setTimeout(() => URL.revokeObjectURL(url), 100);
        setDownloading(null);
      }, 'image/png');
    } catch (e) {
      alert(t('qr_code.err_download_png', { message: e.message }));
      setDownloading(null);
    }
  }, [fileSlug, t]);

  /** Télécharger juste le QR en SVG (pour réutilisation graphique). */
  const handleDownloadSVG = useCallback(async () => {
    setDownloading('svg');
    try {
      const svg = document.querySelector('#qr-code-div svg');
      if (!svg) throw new Error(t('qr_code.err_svg_not_found'));
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svg);
      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      triggerDownload(url, `revyo-qr-${fileSlug}.svg`);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (e) {
      alert(t('qr_code.err_download_svg', { message: e.message }));
    } finally {
      setDownloading(null);
    }
  }, [fileSlug, t]);

  /** PDF : capture la carte → fenêtre A4 + window.print() pour "Save as PDF". */
  const handleDownloadPDF = useCallback(async () => {
    setDownloading('pdf');
    try {
      if (!stickerRef.current) throw new Error(t('qr_code.err_sticker_not_found'));
      const canvas = await html2canvas(stickerRef.current, {
        backgroundColor: '#FFFFFF',
        scale: 3,
        useCORS: true,
      });
      const imgData = canvas.toDataURL('image/png');

      const win = window.open('', '_blank', 'width=820,height=1080');
      if (!win) throw new Error(t('qr_code.err_popup_blocked'));
      const docTitle = t('qr_code.pdf_doc_title', { name: restoName });
      const altText = t('qr_code.qr_alt');
      const footer = t('qr_code.footer_branding');
      win.document.write(`
        <!DOCTYPE html>
        <html><head>
          <title>${docTitle}</title>
          <style>
            @page { size: A4; margin: 20mm; }
            body { margin: 0; font-family: Inter, sans-serif; text-align: center; padding: 40px; background: #FFF; }
            img { max-width: 80%; height: auto; display: block; margin: 20px auto; }
            .footer { margin-top: 30px; font-size: 12px; color: #ABA99F; }
          </style>
        </head><body>
          <img src="${imgData}" alt="${altText}"/>
          <div class="footer">${footer}</div>
          <script>window.onload = () => { window.print(); }<\/script>
        </body></html>
      `);
      win.document.close();
    } catch (e) {
      alert(t('qr_code.err_pdf_generation', { message: e.message }));
    } finally {
      setDownloading(null);
    }
  }, [restoName, t]);

  /** Imprimer la carte (fenêtre A4 + print + close auto). */
  const handlePrint = useCallback(async () => {
    setDownloading('print');
    try {
      if (!stickerRef.current) throw new Error(t('qr_code.err_sticker_not_found'));
      const canvas = await html2canvas(stickerRef.current, {
        backgroundColor: '#FFFFFF',
        scale: 3,
        useCORS: true,
      });
      const imgData = canvas.toDataURL('image/png');

      const win = window.open('', '_blank', 'width=820,height=1080');
      if (!win) throw new Error(t('qr_code.err_popup_blocked'));
      const docTitle = t('qr_code.pdf_doc_title', { name: restoName });
      const altText = t('qr_code.qr_alt');
      const footer = t('qr_code.footer_branding');
      win.document.write(`
        <!DOCTYPE html>
        <html><head>
          <title>${docTitle}</title>
          <style>
            @page { size: A4; margin: 20mm; }
            body { margin: 0; font-family: Inter, sans-serif; text-align: center; padding: 40px; background: #FFF; }
            img { max-width: 85%; height: auto; display: block; margin: 20px auto; }
            .footer { margin-top: 30px; font-size: 13px; color: #ABA99F; }
          </style>
        </head><body>
          <img src="${imgData}" alt="${altText}"/>
          <div class="footer">${footer}</div>
          <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 600); }<\/script>
        </body></html>
      `);
      win.document.close();
    } catch (e) {
      alert(t('qr_code.err_print', { message: e.message }));
    } finally {
      setDownloading(null);
    }
  }, [restoName, t]);

  /** Simuler le scan client : ouvre la page /r/{slug} dans un nouvel onglet. */
  const handleSimulate = useCallback(() => {
    window.open(reviewLink, '_blank', 'noopener,noreferrer');
  }, [reviewLink]);

  const conversion = `${(stats.tauxConversion ?? 0).toFixed(1)}%`;

  return (
    <>
      <MetaHead page="qr_code" />
      <header className={styles.tabHeader}>
        <div className={styles.titleBlock}>
          <h1><span className={styles.titleEmoji}>{t('qr_code.title_emoji')}</span>{t('qr_code.title')}</h1>
          <p>{t('qr_code.subtitle')}</p>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnPrimaryAction}
            onClick={handleDownloadPNG}
            disabled={downloading === 'png'}
          >
            {downloading === 'png' ? t('qr_code.btn_download_sticker_loading') : t('qr_code.btn_download_sticker')}
          </button>
        </div>
      </header>

      <div className={styles.grid}>
        {/* ===== STICKER CARD (téléchargée en entier) ===== */}
        <section id="qr-container" className={styles.qrCard}>
          <div ref={stickerRef} className={styles.stickerWrap}>
            {/* Logo Revyo en haut */}
            <div className={styles.stickerBrand}>
              <div className={styles.stickerLogo}>R</div>
              <div className={styles.stickerBrandName}>Revyo</div>
            </div>

            {/* Label */}
            <div className={styles.stickerLabel}>{t('qr_code.sticker_brand_label')}</div>

            {/* QR avec R au centre */}
            <div className={styles.stickerQrFrame}>
              <div id="qr-code-div" className={styles.stickerQrInner}>
                <QRCodeSVG
                  value={reviewLink}
                  size={260}
                  level="H"
                  bgColor="#FFFFFF"
                  fgColor="#18160F"
                  imageSettings={{
                    src: REVYO_LOGO_SVG,
                    height: 50,
                    width: 50,
                    excavate: true,
                  }}
                />
              </div>
            </div>

            {/* Nom resto + tagline */}
            <div id="qr-resto-name" className={styles.stickerResto}>{restoName}</div>
            <div className={styles.stickerTagline}>{t('qr_code.sticker_tagline')}</div>

            {/* Bandeau étoile */}
            <div className={styles.stickerBanner}>
              <span className={styles.stickerBannerStar}>⭐</span>
              <span>{t('qr_code.sticker_banner_text')}</span>
            </div>

            {/* Footer "Fonctionne avec..." */}
            <div className={styles.stickerFooter}>
              {t('qr_code.sticker_footer_line1')}<br />
              {t('qr_code.sticker_footer_line2')}
            </div>
          </div>

          {/* QRCodeCanvas caché 1024px (au cas où on veut un PNG juste-QR plus tard) */}
          <div ref={hiddenCanvasRef} style={{ position: 'absolute', left: '-99999px', top: '-99999px' }}>
            <QRCodeCanvas
              value={reviewLink}
              size={1024}
              level="H"
              bgColor="#FFFFFF"
              fgColor="#18160F"
              imageSettings={{ src: REVYO_LOGO_SVG, height: 200, width: 200, excavate: true }}
            />
          </div>

          {/* Actions sous le sticker (hors capture) */}
          <button
            type="button"
            className={styles.btnSimulate}
            onClick={handleSimulate}
          >
            {t('qr_code.btn_simulate')}
          </button>

          <div className={styles.dlRow}>
            <button type="button" className={styles.btnGhost} onClick={handleDownloadPNG} disabled={downloading === 'png'}>
              ⬇ {downloading === 'png' ? '…' : t('qr_code.btn_dl_png')}
            </button>
            <button type="button" className={styles.btnGhost} onClick={handleDownloadSVG} disabled={downloading === 'svg'}>
              ⬇ {downloading === 'svg' ? '…' : t('qr_code.btn_dl_svg')}
            </button>
            <button type="button" className={styles.btnGhost} onClick={handleDownloadPDF} disabled={downloading === 'pdf'}>
              ⬇ {downloading === 'pdf' ? '…' : t('qr_code.btn_dl_pdf')}
            </button>
            <button type="button" className={styles.btnGhost} onClick={handlePrint} disabled={downloading === 'print'}>
              🖨️ {downloading === 'print' ? '…' : t('qr_code.btn_dl_print')}
            </button>
          </div>
        </section>

        {/* ===== KPIs ===== */}
        <section className={`${styles.card} ${styles.kpiCard}`}>
          <header className={styles.cardHead}>
            <h3>{t('qr_code.kpi_section_title')}</h3>
            <span className={styles.cardSub}>{t('qr_code.kpi_section_sub')}</span>
          </header>
          <div className={styles.kpiGrid}>
            <div id="kpi-scans"><KPI icon="📲" label={t('qr_code.kpi_scans')} value={stats.scans ?? 0} accent="rgba(108,140,180,0.12)" /></div>
            <div id="qr-avis-generes"><KPI icon="⭐" label={t('qr_code.kpi_avis_generes')} value={stats.avisGeneres ?? 0} accent="rgba(212,165,116,0.18)" /></div>
            <div id="qr-taux-conversion"><KPI icon="🎯" label={t('qr_code.kpi_conversion')} value={conversion} accent="rgba(232,80,42,0.12)" /></div>
            <div id="qr-feedbacks"><KPI icon="💬" label={t('qr_code.kpi_feedbacks')} value={stats.feedbacks ?? 0} accent="rgba(122,154,107,0.15)" /></div>
          </div>
        </section>

        {/* ===== Lien direct ===== */}
        <section className={`${styles.card} ${styles.linkCard}`}>
          <header className={styles.cardHead}>
            <h3>{t('qr_code.link_section_title')}</h3>
            <span className={styles.cardSub}>{t('qr_code.link_section_sub')}</span>
          </header>
          <div className={styles.linkRow}>
            <input
              ref={inputRef}
              id="qr-link-text"
              className={styles.linkInput}
              value={reviewLink}
              readOnly
              onClick={(e) => e.target.select()}
            />
            <button
              type="button"
              className={`${styles.btnPrimary} ${copied ? styles.btnCopied : ''}`}
              onClick={handleCopy}
            >
              {copied ? t('qr_code.btn_copied') : t('qr_code.btn_copy')}
            </button>
          </div>
        </section>

        {/* ===== Guide ===== */}
        <section className={`${styles.card} ${styles.guideCard}`}>
          <header className={styles.cardHead}>
            <h3>{t('qr_code.guide_title')}</h3>
            <span className={styles.cardSub}>{t('qr_code.guide_sub')}</span>
          </header>
          <ul className={styles.guideList}>
            <li><span className={styles.guideEmoji}>🧾</span><div><strong>{t('qr_code.guide_item_ticket_title')}</strong><span>{t('qr_code.guide_item_ticket_desc')}</span></div></li>
            <li><span className={styles.guideEmoji}>🪟</span><div><strong>{t('qr_code.guide_item_vitrine_title')}</strong><span>{t('qr_code.guide_item_vitrine_desc')}</span></div></li>
            <li><span className={styles.guideEmoji}>📋</span><div><strong>{t('qr_code.guide_item_table_title')}</strong><span>{t('qr_code.guide_item_table_desc')}</span></div></li>
            <li><span className={styles.guideEmoji}>📦</span><div><strong>{t('qr_code.guide_item_pack_title')}</strong><span>{t('qr_code.guide_item_pack_desc')}</span></div></li>
          </ul>
        </section>
      </div>
    </>
  );
}
